/**
 * «Семейная ипотека» по правилам с 01.10.2026 — единый расчёт для сервера и виджетов.
 *
 * До 1 октября у программы была одна ставка (до 6 %) и один лимит, поэтому её
 * считали как любую другую: «ставка банка × аннуитет». С 1 октября ставка и
 * лимит кредита зависят от ЧИСЛА ДЕТЕЙ и РЕГИОНА, льготная ставка действует не
 * дольше 15 лет, а сумма сверх лимита выдаётся только по рыночной ставке.
 * Одного числа «ставка программы» больше не существует.
 *
 * Чем это обернулось в бою (замер 12.10.2026, Игротэк, Владимир): витрина
 * banki.ru отдаёт по банку одно число «от» — 3,99–4 % (ярус семьи с четырьмя
 * детьми). Парсер честно его снял, и все три виджета считали «Семейную» по
 * 3,99 % для любого покупателя. Семье с одним ребёнком банк даст 10 %: платёж
 * на витрине был занижен почти вдвое.
 *
 * Поэтому ставка «Семейной» берётся НЕ с витрины банков, а из сетки программы.
 * Витрина по-прежнему отвечает на вопрос «какие банки выдают программу в этом
 * городе и с каким взносом», но не на вопрос «под сколько».
 *
 * Модуль лежит в shared-ui, потому что считать обязаны одинаково: калькулятор
 * (в браузере), витрина и чат (на сервере) и пересчёт заявки перед отправкой в
 * CRM. Вторая копия формулы разойдётся — так уже расходились ставки (24.08.2026)
 * и маска телефона.
 *
 * Источники условий (сверено 12.10.2026):
 *   • Минфин России, «Утверждены изменения в программу „Семейная ипотека“» —
 *     сетка ставок и лимитов, срок субсидирования, дата вступления;
 *   • спроси.дом.рф (оператор программы), инструкция «Семейная ипотека» —
 *     ставка не выше 6 % при оплате 50 % цены и при ребёнке-инвалиде, правило
 *     ставки после 15 лет, комбинированная ипотека и её потолок;
 *   • Банки.ру, разбор изменений с 1 октября — вторая сверка тех же правил.
 */

/** Группа региона: четыре столичных субъекта и вся остальная Россия. */
export type FamilyRegion = 'capital' | 'regions';

/** Ярус программы: сколько детей — такая ставка и такой лимит льготного кредита. */
export interface FamilyTier {
  /** Число детей; 5 означает «пять и более». */
  children: number;
  /** Предельная ставка яруса, % годовых. */
  ratePct: number;
  /** Максимальная сумма кредита по льготной ставке, ₽. */
  maxLoan: number;
}

/** Условия программы для одного региона — то, что уезжает в конфиг виджета. */
export interface FamilyTerms {
  region: FamilyRegion;
  /** С какой даты действует эта редакция условий (ISO). */
  effectiveFrom: string;
  tiers: FamilyTier[];
  /** Ставка «не выше» при взносе от `reducedDownPct` или ребёнке-инвалиде, %. */
  reducedRatePct: number;
  /** Доля цены, оплата которой при сделке даёт ставку не выше `reducedRatePct`, %. */
  reducedDownPct: number;
  /** Сколько лет государство субсидирует ставку. */
  subsidyYears: number;
  /** На сколько п.п. ставка может вырасти после окончания субсидии (верхняя граница). */
  afterSubsidyAddPct: number;
  /** Потолок всего кредита при комбинированной ипотеке (льготная часть + рыночная), ₽. */
  combinedMaxLoan: number;
}

const MLN = 1_000_000;

/**
 * Сетка программы. Правится ТОЛЬКО по новой редакции постановления: каждое
 * число здесь — обещание платежа покупателю на сайте застройщика.
 */
export const FAMILY_PROGRAM = {
  effectiveFrom: '2026-10-01',
  tiers: {
    // Вся Россия, кроме Москвы, Санкт-Петербурга, Московской и Ленинградской областей.
    regions: [
      { children: 1, ratePct: 10, maxLoan: 6 * MLN },
      { children: 2, ratePct: 8, maxLoan: 8 * MLN },
      { children: 3, ratePct: 6, maxLoan: 10 * MLN },
      { children: 4, ratePct: 4, maxLoan: 10 * MLN },
      { children: 5, ratePct: 2, maxLoan: 10 * MLN },
    ],
    // Москва, Санкт-Петербург, Московская и Ленинградская области.
    capital: [
      { children: 1, ratePct: 12, maxLoan: 12 * MLN },
      { children: 2, ratePct: 10, maxLoan: 15 * MLN },
      { children: 3, ratePct: 8, maxLoan: 18 * MLN },
      { children: 4, ratePct: 6, maxLoan: 18 * MLN },
      { children: 5, ratePct: 4, maxLoan: 18 * MLN },
    ],
  },
  reducedRatePct: 6,
  reducedDownPct: 50,
  subsidyYears: 15,
  afterSubsidyAddPct: 4,
  combinedMaxLoan: { regions: 15 * MLN, capital: 30 * MLN },
  /** Минимальное и максимальное число детей в сетке («5» = пять и более). */
  minChildren: 1,
  maxChildren: 5,
} as const;

/**
 * Города столичной группы по slug'у витрины (`tenants.city_slug`).
 *
 * В справочнике городов парсера из четырёх столичных субъектов есть только сами
 * Москва и Санкт-Петербург; городов Московской и Ленинградской областей в нём
 * нет. Застройщик из Химок или Мурино ставит группу настройкой аккаунта
 * (`override`), а не ждёт, пока его город появится в этом списке.
 */
const CAPITAL_CITY_SLUGS: ReadonlySet<string> = new Set(['moskva', 'sankt-peterburg']);

/** Группа региона тенанта: явная настройка аккаунта бьёт определение по городу. */
export function familyRegionOf(citySlug?: string | null, override?: unknown): FamilyRegion {
  if (override === 'capital' || override === 'regions') return override;
  return citySlug && CAPITAL_CITY_SLUGS.has(citySlug) ? 'capital' : 'regions';
}

/**
 * Привести число детей к ярусу сетки: 1…5, где 5 — «пять и более».
 *
 * Мусор и пустое значение дают `fallback` (по умолчанию один ребёнок — самый
 * дорогой ярус): недостающее число детей не должно удешевлять платёж.
 */
export function normalizeChildren(value: unknown, fallback: number = FAMILY_PROGRAM.minChildren): number {
  const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
  const base = Number.isFinite(n) ? Math.floor(n) : fallback;
  return Math.min(FAMILY_PROGRAM.maxChildren, Math.max(FAMILY_PROGRAM.minChildren, base));
}

/** Условия программы для региона — плоский объект, пригодный для JSON-конфига. */
export function familyTerms(region: FamilyRegion): FamilyTerms {
  return {
    region,
    effectiveFrom: FAMILY_PROGRAM.effectiveFrom,
    tiers: FAMILY_PROGRAM.tiers[region].map((t) => ({ ...t })),
    reducedRatePct: FAMILY_PROGRAM.reducedRatePct,
    reducedDownPct: FAMILY_PROGRAM.reducedDownPct,
    subsidyYears: FAMILY_PROGRAM.subsidyYears,
    afterSubsidyAddPct: FAMILY_PROGRAM.afterSubsidyAddPct,
    combinedMaxLoan: FAMILY_PROGRAM.combinedMaxLoan[region],
  };
}

/** Что известно о семье покупателя в момент расчёта. */
export interface FamilyBuyer {
  /** Число детей (любое число; приводится к ярусу 1…5). */
  children?: unknown;
  /** Первоначальный взнос, % от цены. Не задан → льгота «взнос от 50 %» не применяется. */
  downPct?: number;
  /** В семье есть ребёнок с инвалидностью. */
  disabledChild?: boolean;
}

/** Ставка и лимит программы для конкретной семьи. */
export interface FamilyQuote {
  /** Ярус, по которому посчитано (1…5). */
  children: number;
  /** Ставка программы для этой семьи, % годовых. */
  ratePct: number;
  /** Ставка яруса до применения правила «не выше 6 %». */
  tierRatePct: number;
  /** Лимит льготной части кредита, ₽. */
  maxLoan: number;
  /** Почему ставка ниже ярусной: взнос от 50 % или ребёнок-инвалид. `null` — ярусная. */
  reducedBy: 'down' | 'disabled' | null;
}

/**
 * Ставка и лимит программы для семьи.
 *
 * Правило «не выше 6 %» (взнос от 50 % цены или ребёнок-инвалид) именно
 * ОГРАНИЧИВАЕТ ставку сверху: семье с четырьмя детьми оно её не поднимает с 4
 * до 6 %. Лимит кредита при этом остаётся ярусным — правило про ставку.
 */
export function familyQuote(terms: FamilyTerms, buyer: FamilyBuyer = {}): FamilyQuote {
  const children = normalizeChildren(buyer.children);
  // Ярусы идут по возрастанию числа детей; берём последний, не превышающий запрос.
  let tier = terms.tiers[0]!;
  for (const t of terms.tiers) if (t.children <= children) tier = t;

  let ratePct = tier.ratePct;
  let reducedBy: FamilyQuote['reducedBy'] = null;
  if (ratePct > terms.reducedRatePct) {
    if (buyer.disabledChild === true) {
      ratePct = terms.reducedRatePct;
      reducedBy = 'disabled';
    } else if (typeof buyer.downPct === 'number' && buyer.downPct >= terms.reducedDownPct) {
      ratePct = terms.reducedRatePct;
      reducedBy = 'down';
    }
  }
  return { children: tier.children, ratePct, tierRatePct: tier.ratePct, maxLoan: tier.maxLoan, reducedBy };
}

/** Аннуитетный платёж; ставка в % годовых, срок в месяцах. */
export function annuity(principal: number, annualRatePct: number, months: number): number {
  if (principal <= 0 || months <= 0) return 0;
  if (annualRatePct <= 0) return principal / months;
  const r = annualRatePct / 100 / 12;
  const pow = Math.pow(1 + r, months);
  return (principal * r * pow) / (pow - 1);
}

/** Остаток долга после `paid` аннуитетных платежей. */
function balanceAfter(principal: number, annualRatePct: number, payment: number, paid: number): number {
  if (annualRatePct <= 0) return Math.max(0, principal - payment * paid);
  const r = annualRatePct / 100 / 12;
  const pow = Math.pow(1 + r, paid);
  return Math.max(0, principal * pow - (payment * (pow - 1)) / r);
}

/** Вход расчёта платежа по программе. */
export interface FamilyPaymentInput {
  /** Сумма кредита целиком (цена − взнос), ₽. */
  principal: number;
  /** Срок кредита, месяцев. */
  months: number;
  /** Льготная ставка: из `familyQuote` либо собственная ставка застройщика, % годовых. */
  ratePct: number;
  /** Лимит льготной части, ₽ (из `familyQuote`). */
  maxLoan: number;
  /**
   * Рыночная ставка ЭТОГО банка для части сверх лимита, % годовых. Не задана —
   * кредит сверх лимита посчитать нечем, и предложение считается недоступным.
   */
  marketRatePct?: number | null;
}

/** Почему программа недоступна на этих параметрах. */
export type FamilyUnavailable =
  /** Сумма кредита выше потолка комбинированной ипотеки. */
  | 'over_combined_limit'
  /** Нужна рыночная часть, а рыночной ставки у банка нет. */
  | 'no_market_rate';

export type FamilyPayment =
  | {
      available: true;
      /** Платёж в льготный период (первые `subsidyMonths` месяцев или весь срок), ₽/мес. */
      monthly: number;
      /** Платёж после окончания субсидии — верхняя граница. `null`, если срок в неё укладывается. */
      monthlyAfter: number | null;
      /** Ставка льготной части после окончания субсидии (верхняя граница), %. `null` — не меняется. */
      rateAfterPct: number | null;
      /** Сколько месяцев действует льготная ставка в этом кредите. */
      subsidyMonths: number;
      /** Часть кредита по льготной ставке, ₽. */
      prefLoan: number;
      /** Часть кредита по рыночной ставке (сверх лимита программы), ₽. 0 — вся сумма льготная. */
      marketLoan: number;
      /** Сколько покупатель выплатит банку за весь срок (верхняя оценка), ₽. */
      totalPaid: number;
    }
  | {
      available: false;
      reason: FamilyUnavailable;
      /**
       * До какой суммы кредита программа доступна, ₽: потолок комбинированной
       * ипотеки либо лимит льготной части. Виджет выводит из неё подсказку
       * «увеличьте взнос до N ₽».
       */
      loanCap: number;
    };

/**
 * Платёж по «Семейной» с лимитом и сроком субсидии.
 *
 * Две вещи, которых не знает обычный аннуитет:
 *
 * 1. **Лимит.** Льготная ставка действует на сумму до `maxLoan`. Остальное банк
 *    выдаёт по рыночной ставке одним договором («комбинированная ипотека»), и
 *    весь кредит не может превысить `terms.combinedMaxLoan`. Пример оператора:
 *    трое детей, Саратовская область, 14 млн ₽ — 10 млн под 6 % и 4 млн на
 *    рыночных условиях.
 *
 * 2. **Срок субсидии.** Льготная ставка действует `terms.subsidyYears` лет.
 *    Дальше ставка «не может превышать» меньшее из двух: ставка договора + 4 п.п.
 *    либо ключевая ЦБ + 3–3,5 п.п. Ключевую через 15 лет не знает никто, поэтому
 *    считаем верхнюю границу — ставка договора + `afterSubsidyAddPct`. Меньшее из
 *    двух её не превысит. Платёж до окончания субсидии — аннуитет на ВЕСЬ срок по
 *    льготной ставке, после — остаток долга на оставшийся срок по новой ставке.
 *
 * Рыночная часть считается обычным аннуитетом на весь срок и после 15 лет не
 * меняется: субсидия её не касалась.
 */
export function familyPayment(terms: FamilyTerms, input: FamilyPaymentInput): FamilyPayment {
  const principal = Math.max(0, input.principal);
  const months = Math.max(0, Math.round(input.months));
  const prefLoan = Math.min(principal, input.maxLoan);
  const marketLoan = principal - prefLoan;

  if (principal > terms.combinedMaxLoan) {
    return { available: false, reason: 'over_combined_limit', loanCap: terms.combinedMaxLoan };
  }
  const marketRate = input.marketRatePct;
  const hasMarketRate = typeof marketRate === 'number' && Number.isFinite(marketRate) && marketRate > 0;
  if (marketLoan > 0 && !hasMarketRate) {
    return { available: false, reason: 'no_market_rate', loanCap: input.maxLoan };
  }

  const subsidyMonths = Math.min(months, terms.subsidyYears * 12);
  const prefMonthly = annuity(prefLoan, input.ratePct, months);
  const marketMonthly = marketLoan > 0 ? annuity(marketLoan, marketRate as number, months) : 0;

  let monthlyAfter: number | null = null;
  let rateAfterPct: number | null = null;
  if (months > subsidyMonths && prefLoan > 0) {
    rateAfterPct = input.ratePct + terms.afterSubsidyAddPct;
    const rest = balanceAfter(prefLoan, input.ratePct, prefMonthly, subsidyMonths);
    monthlyAfter = annuity(rest, rateAfterPct, months - subsidyMonths) + marketMonthly;
  }

  const monthly = prefMonthly + marketMonthly;
  const totalPaid =
    monthlyAfter === null ? monthly * months : monthly * subsidyMonths + monthlyAfter * (months - subsidyMonths);

  return { available: true, monthly, monthlyAfter, rateAfterPct, subsidyMonths, prefLoan, marketLoan, totalPaid };
}

/** Предложение банка по «Семейной»: льготная ставка и рыночная — для суммы сверх лимита. */
export interface FamilyBankOffer {
  ratePct: number;
  /** `null` — рыночной ставки у банка нет (или взноса для неё не хватает). */
  marketRatePct: number | null;
}

/** Данные программы, достаточные, чтобы посчитать платёж под любую сумму кредита. */
export interface FamilyProgramData {
  terms: FamilyTerms;
  /** Ярус, по которому посчитаны ставки (1…5). */
  children: number;
  /** Лимит льготной части кредита для этой семьи, ₽. */
  maxLoan: number;
  offers: FamilyBankOffer[];
}

/** Выигравшее предложение: ставки банка и платёж по ним. */
export interface FamilyBestOffer {
  /** Льготная ставка выигравшего банка, %. */
  ratePct: number;
  /** Его рыночная ставка для суммы сверх лимита, %. `null` — у банка её нет. */
  marketRatePct: number | null;
  payment: Extract<FamilyPayment, { available: true }>;
}

/**
 * Лучшее предложение по «Семейной» под сумму кредита — минимальный платёж
 * льготного периода среди банков, у которых программа на этой сумме доступна.
 *
 * `null` — недоступна ни у одного: кредит выше потолка комбинированной ипотеки
 * либо сумма выше лимита, а рыночной ставки нет ни у кого. Такую программу в
 * «платёж от» брать нельзя.
 *
 * `marketRatePct` — рыночная ставка ВЫИГРАВШЕГО банка (та, по которой он выдаёт
 * сумму сверх лимита). Нужна виджету, чтобы объяснить покупателю лот выше лимита:
 * «6 млн ₽ под 10 % + 1,71 млн ₽ под 16,99 %». Применяется только при
 * `payment.marketLoan > 0`; в пределах лимита это просто ставка того же банка.
 *
 * Функция общая для сервера (карточка витрины, чат) и для раскрытой карточки
 * витрины в браузере. Пока карточку считал собственный аннуитет витрины, лот за
 * 9 646 200 ₽ показывал бы на карточке 76 981 ₽, а в раскрытой — 67 637 ₽.
 */
export function familyBestOffer(
  data: FamilyProgramData,
  principal: number,
  months: number,
): FamilyBestOffer | null {
  let best: FamilyBestOffer | null = null;
  for (const offer of data.offers) {
    const payment = familyPayment(data.terms, {
      principal,
      months,
      ratePct: offer.ratePct,
      maxLoan: data.maxLoan,
      marketRatePct: offer.marketRatePct,
    });
    if (!payment.available) continue;
    if (!best || Math.round(payment.monthly) < Math.round(best.payment.monthly)) best = { ratePct: offer.ratePct, marketRatePct: offer.marketRatePct, payment };
  }
  return best;
}

/**
 * Помесячный график платежей — для расчёта ПСК по фактическим потокам.
 * Формула ПСК с одной ставкой на двухэтапном кредите занизила бы её.
 */
export function familySchedule(payment: Extract<FamilyPayment, { available: true }>, months: number): number[] {
  const n = Math.max(0, Math.round(months));
  const out: number[] = [];
  for (let m = 1; m <= n; m += 1) {
    out.push(m <= payment.subsidyMonths || payment.monthlyAfter === null ? payment.monthly : payment.monthlyAfter);
  }
  return out;
}
